/**
 * AudioSeriesScreen — the episode list for one audio series (AUDIO-LESSONS-002).
 *
 * Mirrors RolePlayCollectionScreen: drill-down from the Listen catalog showing a
 * series' published episodes as a vertical list. Each episode is a tappable row
 * that navigates to the AudioPlayer. Episodes with saved progress show a
 * "Resume" affordance; completed episodes show a done marker.
 */

import React, { useCallback, useMemo } from 'react';
import {
  ActivityIndicator,
  FlatList,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';

import { ScreenHeader } from '../components/ui/ScreenHeader';
import { WebContainer } from '../components/ui/WebContainer';
import { useDisplayTheme } from '../hooks/useDisplayTheme';
import { useResolveBilingual, slugToTitle } from '../hooks/useResolveBilingual';
import { formatDuration } from '../utils/formatDuration';
import { errorMessage } from '../generated/api/endpoint-errors';
import {
  useAudioLessonsStore,
  type AudioEpisodeSummary,
  type AudioEpisodeProgress,
} from '../store/audioLessonsStore';
import {
  selectCurrentAudioSeries,
  selectCurrentAudioSeriesSlug,
  selectAudioEpisodes,
  selectIsLoadingAudioEpisodes,
  selectAudioEpisodesError,
  selectAudioProgressByEpisode,
  selectFetchAudioSeries,
  selectClearAudioEpisodesError,
} from '../store/audioLessonsSelectors';

interface EpisodeRowProps {
  episode: AudioEpisodeSummary;
  progress: AudioEpisodeProgress | null;
  onPress: (slug: string) => void;
}

const EpisodeRow = React.memo(function EpisodeRow({ episode, progress, onPress }: EpisodeRowProps) {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const { t } = useTranslation();
  const resolve = useResolveBilingual();
  const handlePress = useCallback(() => onPress(episode.slug), [onPress, episode.slug]);

  const title = resolve(episode.title) || slugToTitle(episode.slug);
  const completed = progress?.completed === true;
  const resumable = !completed && (progress?.positionMs ?? 0) > 0;

  return (
    <TouchableOpacity
      testID={`audio-series-episode-${episode.slug}`}
      accessibilityRole="button"
      accessibilityLabel={t('audioLessons.series.episodeLabel', { title })}
      onPress={handlePress}
      activeOpacity={0.75}
    >
      <View style={[styles.row, { backgroundColor: theme.card, borderColor: theme.border }]}>
        <View style={{ flex: 1 }}>
          <Text
            allowFontScaling={false}
            numberOfLines={1}
            style={{ fontSize: 16 * fontScale, fontWeight: '700', color: theme.text, ...ffs }}
          >
            {title}
          </Text>
          {episode.grammarFocus.length > 0 && (
            <Text
              allowFontScaling={false}
              numberOfLines={2}
              style={{ fontSize: 13 * fontScale, color: theme.textSecondary, marginTop: 4, ...ffs }}
            >
              {episode.grammarFocus}
            </Text>
          )}
          <View style={styles.metaRow}>
            {episode.durationMs != null && (
              <Text
                allowFontScaling={false}
                style={{ fontSize: 12 * fontScale, color: theme.textSecondary, ...ffs }}
              >
                {formatDuration(episode.durationMs)}
              </Text>
            )}
            {completed && (
              <Text
                allowFontScaling={false}
                style={{ fontSize: 12 * fontScale, color: theme.primary, fontWeight: '600', ...ffs }}
              >
                {t('audioLessons.series.completed')}
              </Text>
            )}
            {resumable && (
              <Text
                allowFontScaling={false}
                style={{ fontSize: 12 * fontScale, color: theme.primary, fontWeight: '600', ...ffs }}
              >
                {t('audioLessons.series.resume')}
              </Text>
            )}
          </View>
        </View>
        {episode.isPremium && (
          <View style={[styles.premiumPill, { backgroundColor: theme.background, borderColor: theme.border }]}>
            <Text
              allowFontScaling={false}
              style={{ fontSize: 11 * fontScale, fontWeight: '700', color: theme.textSecondary, ...ffs }}
            >
              {t('audioLessons.series.premium')}
            </Text>
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
});

export function AudioSeriesScreen() {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const navigation = useNavigation();
  const route = useRoute();
  const slug = (route.params as { slug: string }).slug;
  const resolve = useResolveBilingual();

  const storeSlug = useAudioLessonsStore(selectCurrentAudioSeriesSlug);
  const allEpisodes = useAudioLessonsStore(selectAudioEpisodes);
  const storeSeries = useAudioLessonsStore(selectCurrentAudioSeries);
  const isLoading = useAudioLessonsStore(selectIsLoadingAudioEpisodes);
  const errorInfo = useAudioLessonsStore(selectAudioEpisodesError);
  const progressByEpisode = useAudioLessonsStore(selectAudioProgressByEpisode);
  const fetchSeries = useAudioLessonsStore(selectFetchAudioSeries);
  const clearError = useAudioLessonsStore(selectClearAudioEpisodesError);

  // The store holds ONE series' episodes at a time; only show them when they
  // belong to THIS screen's slug (else a sibling series' data could flash).
  const isThisSeries = storeSlug === slug;
  const episodes = useMemo(() => (isThisSeries ? allEpisodes : []), [isThisSeries, allEpisodes]);
  const series = isThisSeries ? storeSeries : null;

  // Fetch when the store isn't already showing this series.
  useFocusEffect(
    useCallback(() => {
      if (storeSlug !== slug) void fetchSeries(slug);
    }, [storeSlug, slug, fetchSeries]),
  );

  const handleBack = useCallback(() => navigation.goBack(), [navigation]);
  const handleOpenEpisode = useCallback(
    (episodeSlug: string) => navigation.navigate('AudioPlayer', { slug: episodeSlug }),
    [navigation],
  );
  const handleRetry = useCallback(() => {
    clearError();
    void fetchSeries(slug);
  }, [clearError, fetchSeries, slug]);

  const renderItem = useCallback(
    ({ item }: { item: AudioEpisodeSummary }) => (
      <EpisodeRow
        episode={item}
        progress={progressByEpisode[item.slug] ?? null}
        onPress={handleOpenEpisode}
      />
    ),
    [progressByEpisode, handleOpenEpisode],
  );
  const keyExtractor = useCallback((ep: AudioEpisodeSummary) => ep.slug, []);

  const headerTitle = series ? resolve(series.title) || slugToTitle(slug) : slugToTitle(slug);

  return (
    <View style={{ flex: 1, backgroundColor: theme.background }}>
      <ScreenHeader title={headerTitle} onBack={handleBack} />
      <WebContainer variant="page" style={{ flex: 1 }}>
        <View testID="audio-series-screen" style={styles.scrollContent}>
          {errorInfo && (
            <View
              testID="audio-series-error"
              style={[styles.errorBanner, { backgroundColor: theme.card, borderColor: theme.error }]}
            >
              <Text allowFontScaling={false} style={{ flex: 1, fontSize: 14 * fontScale, color: theme.text, ...ffs }}>
                {errorMessage(errorInfo)}
              </Text>
              <TouchableOpacity
                testID="audio-series-error-retry"
                accessibilityRole="button"
                accessibilityLabel={t('audioLessons.loadErrorRetry')}
                onPress={handleRetry}
                style={[styles.retryButton, { borderColor: theme.border }]}
              >
                <Text
                  allowFontScaling={false}
                  style={{ fontSize: 14 * fontScale, color: theme.text, fontWeight: '600', ...ffs }}
                >
                  {t('audioLessons.loadErrorRetry')}
                </Text>
              </TouchableOpacity>
            </View>
          )}

          {!errorInfo && isLoading && episodes.length === 0 && (
            <ActivityIndicator size="small" color={theme.primary} style={{ paddingVertical: 24 }} />
          )}

          {!errorInfo && !isLoading && episodes.length === 0 && (
            <View testID="audio-series-empty" style={styles.emptyHint}>
              <Text
                allowFontScaling={false}
                style={{ fontSize: 14 * fontScale, color: theme.textSecondary, textAlign: 'center', ...ffs }}
              >
                {t('audioLessons.series.emptyHint')}
              </Text>
            </View>
          )}

          {episodes.length > 0 && (
            <FlatList
              data={episodes}
              renderItem={renderItem}
              keyExtractor={keyExtractor}
              contentContainerStyle={{ gap: 8 }}
            />
          )}
        </View>
      </WebContainer>
    </View>
  );
}

const styles = StyleSheet.create({
  scrollContent: { flex: 1, padding: 16 },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
    gap: 8,
  },
  metaRow: { flexDirection: 'row', alignItems: 'center', gap: 12, marginTop: 6 },
  premiumPill: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    gap: 8,
    marginBottom: 8,
  },
  retryButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
  },
  emptyHint: { alignItems: 'center', paddingVertical: 24 },
});
